import { useCallback, useEffect, useRef, useState } from "react";

function getSpeechRecognition() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function clipText(text, maxLength) {
  const raw = String(text || "");
  if (!maxLength || maxLength <= 0 || raw.length <= maxLength) return raw;
  return raw.slice(0, maxLength);
}

/**
 * AI 输入框旁的语音输入（Web Speech API）
 * - 连续识别并累加完整内容（不再只保留最后一段）
 * - 可用 maxLength 限制总字数
 */
export default function SpeechInputButton({
  onResult,
  disabled = false,
  className = "",
  /** 最大字数，默认 2000 */
  maxLength = 2000,
  /** 开始录音时取一次前缀（如输入框已有文字），语音接在后面 */
  getPrefix,
}) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const [hint, setHint] = useState("");
  const recognitionRef = useRef(null);
  const textRef = useRef("");
  const finalAccRef = useRef("");
  const prefixRef = useRef("");
  const commitRef = useRef(false);
  const wantListenRef = useRef(false);
  const onResultRef = useRef(onResult);
  const getPrefixRef = useRef(getPrefix);

  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  useEffect(() => {
    getPrefixRef.current = getPrefix;
  }, [getPrefix]);

  useEffect(() => {
    setSupported(Boolean(getSpeechRecognition()));
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    if (/MicroMessenger/i.test(ua) && !getSpeechRecognition()) {
      setHint("微信内请直接打字留言");
    }
  }, []);

  useEffect(() => {
    return () => {
      wantListenRef.current = false;
      try {
        recognitionRef.current?.abort?.();
      } catch {
        /* ignore */
      }
    };
  }, []);

  const emit = useCallback(
    (speechBody, meta = {}) => {
      const prefix = prefixRef.current;
      const joined = prefix
        ? `${prefix}${/[\s\n]$/.test(prefix) ? "" : ""}${speechBody}`
        : speechBody;
      const clipped = clipText(joined.trim(), maxLength);
      textRef.current = clipped;
      if (
        maxLength > 0 &&
        joined.trim().length > maxLength &&
        !meta.silentHint
      ) {
        setHint(`已达上限 ${maxLength} 字，请结束或删减后再说`);
      }
      if (clipped) {
        onResultRef.current?.(clipped, meta);
      }
    },
    [maxLength]
  );

  const stop = useCallback((commit = false) => {
    commitRef.current = commit;
    wantListenRef.current = false;
    try {
      recognitionRef.current?.stop?.();
    } catch {
      setListening(false);
    }
  }, []);

  const startRecognition = useCallback(() => {
    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = "zh-CN";
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setListening(true);

    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const piece = event.results[i][0]?.transcript || "";
        if (event.results[i].isFinal) {
          finalAccRef.current += piece;
        } else {
          interim += piece;
        }
      }
      const speechBody = `${finalAccRef.current}${interim}`;
      emit(speechBody, {
        interim: Boolean(interim),
        final: false,
      });
    };

    recognition.onerror = (event) => {
      if (event.error === "aborted" || event.error === "no-speech") {
        return;
      }
      if (event.error !== "aborted") {
        setHint("语音识别失败，请改用打字留言");
        setTimeout(() => setHint(""), 3000);
      }
      if (event.error === "not-allowed") {
        wantListenRef.current = false;
        setListening(false);
      }
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      if (wantListenRef.current && !commitRef.current) {
        try {
          startRecognition();
          return;
        } catch {
          /* fall through */
        }
      }

      setListening(false);
      const finalText = textRef.current.trim();
      if (commitRef.current && finalText) {
        onResultRef.current?.(finalText, { interim: false, final: true });
      }
      commitRef.current = false;
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [emit]);

  const start = useCallback(() => {
    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition) {
      setHint(
        /MicroMessenger/i.test(navigator.userAgent || "")
          ? "微信内不支持语音输入，请直接打字"
          : "请用 Chrome / Edge 浏览器使用语音输入"
      );
      setTimeout(() => setHint(""), 3500);
      return;
    }
    if (disabled || wantListenRef.current) return;

    const prefix =
      typeof getPrefixRef.current === "function"
        ? String(getPrefixRef.current() || "")
        : "";
    prefixRef.current = prefix;
    finalAccRef.current = "";
    textRef.current = prefix.trim();
    commitRef.current = false;
    wantListenRef.current = true;
    setHint("");

    try {
      startRecognition();
    } catch {
      wantListenRef.current = false;
      setHint("无法启动麦克风，请改用打字留言");
      setTimeout(() => setHint(""), 3000);
    }
  }, [disabled, startRecognition]);

  if (!supported) {
    return (
      <div className={`relative ${className}`}>
        <button
          type="button"
          title="当前环境不支持语音，请直接打字"
          className="px-3 py-3 rounded-full border text-gray-400 text-sm bg-white"
          onClick={() => {
            setHint(
              /MicroMessenger/i.test(navigator.userAgent || "")
                ? "微信内请直接打字留言"
                : "请用 Chrome / Edge，或直接打字"
            );
            setTimeout(() => setHint(""), 3000);
          }}
        >
          🎤
        </button>
        {hint && (
          <div className="absolute bottom-full right-0 mb-2 max-w-[11rem] text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1 shadow z-10">
            {hint}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        title={
          listening
            ? "点击结束，保留全部识别文字"
            : `点击开始语音输入（最长约 ${maxLength} 字）`
        }
        onClick={() => {
          if (listening || wantListenRef.current) stop(true);
          else start();
        }}
        className={`px-3 py-3 rounded-full border text-sm transition disabled:opacity-50 ${
          listening
            ? "bg-rose-500 text-white border-rose-500 animate-pulse"
            : "border-gray-200 text-gray-600 hover:border-rose-300 hover:text-rose-600"
        }`}
      >
        {listening ? "⏹" : "🎤"}
      </button>
      {listening && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap text-[11px] text-rose-600 bg-white border border-rose-100 rounded-lg px-2 py-1 shadow">
          正在听…内容会连续累加，再点一次结束
        </div>
      )}
      {hint && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1 shadow">
          {hint}
        </div>
      )}
    </div>
  );
}
